if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const crossSell = ProductForm.findCrossSellWidget(this.form, this);
      const hasCrossSellWidget = Boolean(crossSell);
      const crossSellItems = ProductForm.getCrossSellItems(crossSell);
      const mainLineItem = ProductForm.buildLineItemFromFormData(formData);
      const useCrossSellPayload = crossSellItems.length > 0 && mainLineItem;
      if (useCrossSellPayload) {
        this.form.dataset.bbBundleThemeHandledAt = String(Date.now());
      }
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      let fetchBody = formData;
      const fetchHeaders = {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      };

      if (useCrossSellPayload) {
        fetchBody = JSON.stringify({
          items: [
            mainLineItem,
            ...crossSellItems
          ],
          sections_url: window.location.pathname,
          sections
        });
        fetchHeaders['Content-Type'] = 'application/json';
      } else {
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);
      }

      const fetchRequestOpts = {
        method: 'POST',
        headers: fetchHeaders,
        body: fetchBody
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page' && !hasCrossSellWidget) {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                hasCrossSellWidget || (!quickAddDrawer && theme.settings.afterAtc === 'drawer')
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const addedItems = Array.isArray(data.items) ? data.items : [data];
        addedItems.forEach((addedItem) => {
          const addedVariantId = addedItem.variant_id || addedItem.id;
          const itemInOldCart = oldCartData.items.filter(
            (item) => item.variant_id === addedVariantId
          )[0];

          // Check if product was already in the cart
          if (itemInOldCart) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                variantId: addedVariantId,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: (itemInOldCart.quantity === addedItem.quantity)
                  ? itemInOldCart.quantity : addedItem.quantity
              }
            }));
          } else {
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                variantId: addedVariantId
              }
            }));
          }
        });
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections) {
        cartIconBubble.innerHTML = ProductForm.getSectionHTML(
          response.sections['cart-icon-bubble'],
          '.shopify-section'
        );
        ProductForm.normalizeCartIconBubble(cartIconBubble);
      }
    }

    /**
     * Finds the product bundle widget associated with the product form.
     * @param {HTMLFormElement} form - Product form.
     * @param {HTMLElement} productForm - Product form custom element.
     * @returns {?HTMLElement}
     */
    static findCrossSellWidget(form, productForm) {
      return form.querySelector('bb-bundle') || productForm.querySelector('bb-bundle');
    }

    /**
     * Returns selected bundle items. Falls back to DOM parsing if the bb-bundle
     * custom element has not finished loading yet.
     * @param {?HTMLElement} crossSell - Bundle widget.
     * @returns {Array<{id: number, quantity: number}>}
     */
    static getCrossSellItems(crossSell) {
      if (!crossSell) return [];

      if (typeof crossSell.getSelectedItems === 'function') {
        const methodItems = ProductForm.normalizeLineItems(crossSell.getSelectedItems());
        const hasCheckedRows = Boolean(crossSell.querySelector('[data-bb-bundle-checkbox]:checked:not(:disabled)'));
        if (methodItems.length > 0 || !hasCheckedRows) return methodItems;
      }

      const rows = Array.from(crossSell.querySelectorAll('[data-bb-bundle-item]'));
      return ProductForm.normalizeLineItems(rows.reduce((items, row) => {
        const checkbox = row.querySelector('[data-bb-bundle-checkbox]');
        if (!checkbox || !checkbox.checked || checkbox.disabled) return items;

        const select = row.querySelector('[data-bb-bundle-select]');
        const selectedOption = select?.selectedOptions?.[0] || null;
        const variantId = parseInt(select?.value || checkbox.dataset.variantId, 10);
        const isAvailable = selectedOption ? selectedOption.dataset.available !== 'false' : true;

        if (variantId && isAvailable) {
          items.push({ id: variantId, quantity: 1 });
        }

        return items;
      }, []));
    }

    /**
     * Normalizes item payloads before sending them to Shopify.
     * @param {Array} items - Candidate line items.
     * @returns {Array<{id: number, quantity: number}>}
     */
    static normalizeLineItems(items) {
      if (!Array.isArray(items)) return [];

      return items.reduce((normalized, item) => {
        const id = parseInt(item.id, 10);
        const quantity = Math.max(parseInt(item.quantity, 10) || 1, 1);
        if (id) normalized.push({ id, quantity });
        return normalized;
      }, []);
    }

    /**
     * Gets section inner HTML from Shopify Section Rendering API responses.
     * @param {string} html - Section HTML.
     * @param {string} selector - Section wrapper selector.
     * @returns {string}
     */
    static getSectionHTML(html, selector) {
      if (!html) return '';

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : tmpl.innerHTML;
    }

    /**
     * Keeps the theme-native cart count badge and removes duplicate legacy badges.
     * @param {HTMLElement} bubble - Cart icon bubble container.
     */
    static normalizeCartIconBubble(bubble) {
      const nativeBadges = Array.from(bubble.querySelectorAll('.header__cart-count'));
      nativeBadges.slice(1).forEach((badge) => badge.remove());

      if (nativeBadges.length > 0) {
        bubble.querySelectorAll('.cart-count-bubble').forEach((badge) => badge.remove());
      }
    }

    /**
     * Convertit le FormData du produit principal en ligne JSON pour /cart/add.js.
     * @param {FormData} formData - Données du formulaire produit.
     * @returns {?object} Ligne panier principale.
     */
    static buildLineItemFromFormData(formData) {
      const id = parseInt(formData.get('id'), 10);
      if (!id) return null;

      const quantity = Math.max(parseInt(formData.get('quantity'), 10) || 1, 1);
      const lineItem = { id, quantity };
      const sellingPlan = formData.get('selling_plan');
      const properties = {};

      if (sellingPlan) lineItem.selling_plan = sellingPlan;

      formData.forEach((value, key) => {
        if (!key.startsWith('properties[') || value === '') return;

        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
      });

      if (Object.keys(properties).length > 0) {
        lineItem.properties = properties;
      }

      return lineItem;
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
